import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApi } from '@/context/AuthContext';
import { formatIndianCurrency, formatDate, getStatusClasses, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, FileText, Send, Trash2, Eye, CheckCircle2, IndianRupee, XCircle } from 'lucide-react';

export default function InvoicesPage() {
  const api = useApi();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');

  useEffect(() => {
    setSearch(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const sendViaWhatsApp = async (invoiceId) => {
    try {
      const response = await api.post(`/whatsapp/send-invoice/${invoiceId}`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send invoice'));
    }
  };

  const updateStatus = async (invoice, newStatus) => {
    let params = { new_status: newStatus };
    if (newStatus === 'partially_paid') {
      const total = invoice.amount + invoice.tax_amount;
      const input = window.prompt(`Amount received so far (invoice total ${formatIndianCurrency(total)}):`, invoice.amount_paid || '');
      if (input === null) return;
      params.amount_paid = parseFloat(String(input).replace(/[₹,]/g, ''));
    }
    try {
      await api.put(`/invoices/${invoice.id}/status`, null, { params });
      toast.success(newStatus === 'paid' ? 'Marked as paid' : newStatus === 'cancelled' ? 'Invoice cancelled' : 'Payment recorded');
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update invoice'));
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.reference_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-stone-200 rounded w-1/3"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Invoices</h1>
            <p className="text-stone-600">Manage and track your invoices</p>
          </div>
          <Link to="/invoices/new">
            <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press gap-2" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4" /> Create Invoice
            </Button>
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="invoice-search"
            />
          </div>
        </div>

        {/* Invoices Table */}
        {filteredInvoices.length > 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-medium font-mono">{invoice.reference_number}</TableCell>
                    <TableCell>{invoice.customer_name}</TableCell>
                    <TableCell>{formatDate(invoice.transaction_date)}</TableCell>
                    <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatIndianCurrency(invoice.amount + invoice.tax_amount)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClasses(invoice.status)}`}>
                        {invoice.status.replace('_', ' ')}
                      </span>
                      {invoice.status === 'partially_paid' && (
                        <p className="text-xs text-stone-500 mt-1 font-mono">{formatIndianCurrency(invoice.amount_paid)} received</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`invoice-actions-${invoice.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/invoices/${invoice.id}`} className="flex items-center">
                              <Eye className="w-4 h-4 mr-2" /> View
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendViaWhatsApp(invoice.id)} data-testid={`invoice-whatsapp-${invoice.id}`}>
                            <Send className="w-4 h-4 mr-2" /> Mark as sent <span className="ml-auto text-[10px] uppercase text-stone-400">WhatsApp soon</span>
                          </DropdownMenuItem>
                          {!['paid', 'cancelled'].includes(invoice.status) && (
                            <>
                              <DropdownMenuItem onClick={() => updateStatus(invoice, 'paid')} data-testid={`invoice-mark-paid-${invoice.id}`}>
                                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as paid
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(invoice, 'partially_paid')} data-testid={`invoice-record-payment-${invoice.id}`}>
                                <IndianRupee className="w-4 h-4 mr-2" /> Record partial payment
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(invoice, 'cancelled')} data-testid={`invoice-cancel-${invoice.id}`}>
                                <XCircle className="w-4 h-4 mr-2" /> Cancel invoice
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem 
                            data-testid={`invoice-delete-${invoice.id}`}
                            onClick={() => deleteInvoice(invoice.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 p-12">
            <div className="text-center">
              <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No invoices yet</h3>
              <p className="text-stone-500 mb-6">Create your first GST-compliant invoice in 60 seconds</p>
              <Link to="/invoices/new">
                <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2">
                  <Plus className="w-4 h-4" /> Create Invoice
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
